import React, { useMemo, useState } from "react";
import { Brain, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { fmtMoney, fmtPct, fmtNum } from "./_utils";

// ---------- Top Payor Risk (premium AI-powered predictive table) ----------
// Tuned for the DARK navy surface-card background (the app runs in `.dark-theme`
// by default). Text colors use lighter 300/400-shades so they hit ≥ 4.5:1
// contrast against the near-black card surface.
const RISK_STYLES = {
  High:   { bg: "rgba(248,113,113,0.16)", border: "rgba(220,38,38,0.55)",  color: "#FCA5A5", dot: "#F87171" },
  Medium: { bg: "rgba(245,158,11,0.16)",  border: "rgba(180,83,9,0.55)",   color: "#FCD34D", dot: "#F59E0B" },
  Low:    { bg: "rgba(34,197,94,0.16)",   border: "rgba(21,128,61,0.55)",  color: "#86EFAC", dot: "#4ADE80" },
};

const RiskBadgeWithTooltip = ({ row, idx }) => {
  const style = RISK_STYLES[row.risk] || {
    bg: "rgba(148,163,184,0.15)", border: "rgba(148,163,184,0.40)", color: "#CBD5E1", dot: "#CBD5E1",
  };
  const confidence = row.risk_confidence ?? 0;
  const drivers = Array.isArray(row.risk_drivers) ? row.risk_drivers : [];
  return (
    <span className="relative inline-block group" data-testid={`payor-risk-badge-${idx}`}>
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider cursor-help"
        style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: style.dot }} />
        {row.risk}
      </span>
      {/* AI hover tooltip — dark surface, teal accent */}
      <span
        role="tooltip"
        data-testid={`payor-risk-tooltip-${idx}`}
        className="pointer-events-none absolute z-30 right-0 mt-2 w-72 origin-top-right scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:scale-100 transition-all duration-150"
        style={{ top: "100%" }}
      >
        <span className="block rounded-lg border border-teal-400/30 shadow-[0_18px_45px_-15px_rgba(20,184,166,0.55)] p-3.5 text-left"
              style={{ background: "linear-gradient(160deg, #0B1524 0%, #122236 60%, #14283F 100%)" }}>
          <span className="flex items-center justify-between gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-teal-200">
              <Sparkles className="h-3 w-3" /> Payor Risk Signal
            </span>
            <span className="text-[10px] font-bold tabular-nums text-slate-200">
              {confidence}% conf
            </span>
          </span>
          <span className="block text-[12px] font-semibold text-white leading-snug mb-2">
            {row.risk} risk · {row.primary_driver || "Mixed drivers"}
          </span>
          <span className="block space-y-1">
            {drivers.map((d, di) => (
              <span key={di} className="flex items-start gap-1.5 text-[11px] text-slate-300 leading-snug">
                <span className="mt-1 h-1 w-1 rounded-full flex-none" style={{ background: style.dot }} />
                <span>{d}</span>
              </span>
            ))}
          </span>
        </span>
      </span>
    </span>
  );
};

const PayorMoMPill = ({ delta }) => {
  if (delta === null || delta === undefined) return <span className="text-slate-400">—</span>;
  const isUp = delta > 0.5;
  const isDown = delta < -0.5;
  const tone = isUp
    ? { color: "#FCA5A5", bg: "rgba(248,113,113,0.14)", border: "rgba(220,38,38,0.40)", arrow: "▲" }
    : isDown
    ? { color: "#86EFAC", bg: "rgba(34,197,94,0.14)",  border: "rgba(21,128,61,0.40)", arrow: "▼" }
    : { color: "#94A3B8", bg: "rgba(148,163,184,0.12)", border: "rgba(100,116,139,0.30)", arrow: "▬" };
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold tabular-nums"
      style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
    >
      <span>{tone.arrow}</span>
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
};

const TopPayorRiskPanel = ({ payorRisk = [], payors = [], denialDrivers = [], onSelect }) => {
  const [open, setOpen] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (payor) => setExpandedRows((m) => ({ ...m, [payor]: !m[payor] }));
  const counts = useMemo(() => {
    const c = { High: 0, Medium: 0, Low: 0 };
    for (const p of payorRisk) {
      if (p.risk in c) c[p.risk] += 1;
    }
    return c;
  }, [payorRisk]);
  const totalRecoverable = useMemo(
    () => payorRisk.reduce((acc, p) => acc + (p.recoverable_dollars || 0), 0),
    [payorRisk]
  );
  // Map payor → topDrivers (top denial categories concentrated on that payor)
  const payorTopDrivers = useMemo(() => {
    const m = {};
    for (const d of denialDrivers || []) {
      const dollars = d.denial_dollars || 0;
      // Approximation: if denial_drivers carries no payor breakdown, assign by primary_driver match
      // We will simply rank globally for now (server-side payor× driver breakdown can come later).
      // Each payor sees the top 3 system-level drivers as a baseline.
      Object.keys(m).length; // no-op for lint
      const entry = { driver: d.driver, dollars, recovery: d.avg_recovery_likelihood, preventable: d.preventable_pct };
      m.__top = m.__top || [];
      m.__top.push(entry);
    }
    m.__top = (m.__top || []).sort((a, b) => b.dollars - a.dollars).slice(0, 3);
    return m;
  }, [denialDrivers]);

  return (
    <div className="surface-card overflow-hidden" data-testid="payor-risk-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="payor-risk-table"
        data-testid="payor-risk-toggle"
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
              <Brain className="h-3 w-3" /> AI Payor Risk Scoring
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1 text-slate-900">
              Top Payor Risk
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Predictive scoring blends denial concentration, aging, preventability & $ exposure. Click any payor to drill down.
            </p>
          </div>
          {/* Risk count chips */}
          <div className="hidden md:flex items-center gap-1.5 ml-2">
            {counts.High > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "rgba(248,113,113,0.18)", border: "1px solid rgba(248,113,113,0.55)", color: "#FCA5A5" }}
                data-testid="payor-risk-count-high"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FCA5A5" }} /> {counts.High} High
              </span>
            )}
            {counts.Medium > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "rgba(251,191,36,0.18)", border: "1px solid rgba(251,191,36,0.55)", color: "#FCD34D" }}
                data-testid="payor-risk-count-medium"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FCD34D" }} /> {counts.Medium} Med
              </span>
            )}
            {counts.Low > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "rgba(74,222,128,0.18)", border: "1px solid rgba(74,222,128,0.55)", color: "#86EFAC" }}
                data-testid="payor-risk-count-low"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#86EFAC" }} /> {counts.Low} Low
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <span className="hidden sm:flex flex-col items-end leading-tight" data-testid="payor-risk-total-recoverable">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Recoverable</span>
            <span className="text-sm font-semibold tabular-nums text-teal-700">{fmtMoney(totalRecoverable, true)}</span>
          </span>
          <span className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center transition-transform" style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}>
            <ChevronDown className="h-4 w-4 text-slate-600" />
          </span>
        </div>
      </button>

      {open && (
        <div id="payor-risk-table" className="border-t border-slate-200" data-testid="payor-risk-table">
          {/* Custom scroll container — allow tooltip overflow on Y, scroll on X */}
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/40">
                  <th className="py-3 pl-5 pr-1 font-semibold w-8"></th>
                  <th className="py-3 px-2 font-semibold">Payor</th>
                  <th className="py-3 px-2 font-semibold text-right">Denial Rate</th>
                  <th className="py-3 px-2 font-semibold text-right">Avg Denial Age</th>
                  <th className="py-3 px-2 font-semibold text-right">Denied $</th>
                  <th className="py-3 px-2 font-semibold text-right">Recoverable $</th>
                  <th className="py-3 px-3 font-semibold">Primary Driver</th>
                  <th className="py-3 px-2 font-semibold text-center">MoM</th>
                  <th className="py-3 px-5 font-semibold text-right">Risk Score</th>
                </tr>
              </thead>
              <tbody>
                {payorRisk.map((p, i) => {
                  const isExpanded = !!expandedRows[p.payor];
                  const payorRow = (payors || []).find((pp) => pp.payor === p.payor);
                  const drivers = payorTopDrivers.__top || [];
                  const aiRec = (() => {
                    if (p.risk === "High") {
                      return `Escalate ${p.payor} immediately. ${p.primary_driver || "Top driver"} concentration combined with ${p.avg_days}d avg aging signals systemic workflow gaps. Schedule a payor performance review and front-end QA sweep within 14 days.`;
                    }
                    if (p.risk === "Medium") {
                      return `Tighten ${p.payor} appeal velocity. With ${fmtMoney(p.recoverable_dollars || 0)} recoverable, prioritize ${p.primary_driver || "the top driver"} in the work queue — expected resolution lift 25–35%.`;
                    }
                    return `${p.payor} is stable. Maintain current playbook; spot-check ${p.primary_driver || "the top driver"} monthly for early drift.`;
                  })();
                  return (
                    <React.Fragment key={p.payor}>
                      <tr
                        data-testid={`payor-risk-row-${i}`}
                        onClick={() => toggleRow(p.payor)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleRow(p.payor);
                          }
                        }}
                        className={`border-b border-slate-100 last:border-b-0 transition-colors cursor-pointer hover:bg-teal-50/60 focus:outline-none focus-visible:bg-teal-50/80 ${isExpanded ? "bg-teal-50/40" : ""}`}
                      >
                        <td className="py-3.5 pl-5 pr-1 align-middle">
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 transition-transform"
                            style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                            data-testid={`payor-risk-row-expand-${i}`}
                          >
                            <ChevronDown className="h-3.5 w-3.5 text-slate-600" />
                          </span>
                        </td>
                        <td className="py-3.5 px-2 font-medium text-slate-900 whitespace-nowrap">{p.payor}</td>
                        <td className="py-3.5 px-2 text-right text-slate-700 font-mono tabular-nums">{fmtPct(p.denial_rate)}</td>
                        <td className="py-3.5 px-2 text-right text-slate-700 font-mono tabular-nums">{p.avg_days}d</td>
                        <td className="py-3.5 px-2 text-right font-mono tabular-nums text-slate-900 font-medium">{fmtMoney(p.dollars, true)}</td>
                        <td className="py-3.5 px-2 text-right font-mono tabular-nums font-semibold" style={{ color: "#2DD4BF" }} data-testid={`payor-risk-recoverable-${i}`}>
                          {fmtMoney(p.recoverable_dollars || 0, true)}
                        </td>
                        <td className="py-3.5 px-3 text-slate-700" data-testid={`payor-risk-primary-driver-${i}`}>
                          <span className="inline-block max-w-[180px] truncate align-bottom" title={p.primary_driver || "—"}>
                            {p.primary_driver || "—"}
                          </span>
                        </td>
                        <td className="py-3.5 px-2 text-center" data-testid={`payor-risk-trend-${i}`}>
                          <PayorMoMPill delta={p.trend_delta_pct} />
                        </td>
                        <td className="py-3.5 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                          <RiskBadgeWithTooltip row={p} idx={i} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr
                          data-testid={`payor-risk-detail-${i}`}
                          className="border-b border-slate-100 last:border-b-0"
                        >
                          <td colSpan={9} className="p-0">
                            <div
                              className="px-5 py-5 border-l-4 animate-in fade-in-0 slide-in-from-top-1 duration-300"
                              style={{ borderLeftColor: "#0F766E", background: "linear-gradient(180deg, rgba(15,118,110,0.04) 0%, rgba(15,118,110,0.01) 100%)" }}
                            >
                              <div className="grid lg:grid-cols-3 gap-5">
                                {/* Risk intelligence */}
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold mb-2" style={{ color: "#2DD4BF" }}>Risk Intelligence</div>
                                  <div className="text-xs text-slate-600 space-y-1.5">
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Risk Confidence</span>
                                      <span className="font-semibold text-slate-900 tabular-nums">{p.risk_confidence ?? 0}%</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Denial Rate</span>
                                      <span className="font-semibold text-slate-900 tabular-nums">{fmtPct(p.denial_rate)}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Avg Denial Age</span>
                                      <span className="font-semibold text-slate-900 tabular-nums">{p.avg_days}d</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Claim Volume</span>
                                      <span className="font-semibold text-slate-900 tabular-nums">{fmtNum(payorRow?.volume || 0)}</span>
                                    </div>
                                  </div>
                                  {Array.isArray(p.risk_drivers) && p.risk_drivers.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">Risk Drivers</div>
                                      <div className="flex flex-wrap gap-1.5">
                                        {p.risk_drivers.map((d, di) => (
                                          <span key={di} className="text-[10.5px] px-1.5 py-0.5 rounded font-medium"
                                                style={{ background: "rgba(15,118,110,0.08)", color: "#2DD4BF", border: "1px solid rgba(15,118,110,0.20)" }}>
                                            {d}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Top denial drivers */}
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold mb-2" style={{ color: "#2DD4BF" }}>Top Denial Drivers</div>
                                  {drivers.length === 0 ? (
                                    <div className="text-xs text-slate-400 italic">No driver data available.</div>
                                  ) : (
                                    <div className="space-y-2">
                                      {drivers.map((d, di) => (
                                        <div key={di} className="flex items-center justify-between gap-3">
                                          <div className="min-w-0">
                                            <div className="text-xs font-medium text-slate-900 truncate" title={d.driver}>{d.driver}</div>
                                            <div className="text-[10.5px] text-slate-500 mt-0.5">
                                              {(d.recovery || 0).toFixed(0)}% recovery · {(d.preventable || 0).toFixed(0)}% preventable
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <div className="text-xs font-mono font-semibold tabular-nums text-slate-900">{fmtMoney(d.dollars, true)}</div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* AI recommendation + CTA */}
                                <div className="rounded-lg p-4 relative overflow-hidden"
                                     style={{ background: "linear-gradient(160deg, #0B1524 0%, #122236 60%, #14283F 100%)", border: "1px solid rgba(20,184,166,0.30)" }}>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Sparkles className="h-3 w-3 text-teal-300" />
                                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-teal-200">Recommendation</span>
                                  </div>
                                  <p className="text-[12px] leading-relaxed text-slate-100">{aiRec}</p>
                                  {onSelect && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); onSelect(p); }}
                                      className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-300 hover:text-teal-200 transition-colors"
                                      data-testid={`payor-risk-drilldown-${i}`}
                                    >
                                      Open full payor intelligence
                                      <ChevronRight className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {payorRisk.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-sm text-slate-400 italic">
                      No payor risk data available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export { TopPayorRiskPanel };
export default TopPayorRiskPanel;
